/**
 * 钉钉文件浏览器组件
 * 支持浏览目录结构并选择Excel文件
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Space, Typography, Spin, Empty, message } from 'dingtalk-design-desktop';
import type { DingTalkFile } from '../utils/dingtalkFilePicker';
import { apiFetch } from '../utils/api';

const { Text } = Typography;

export interface FolderItem {
  folderId: string;
  folderName: string;
  spaceId: string;
  parentFolderId: string;
}

export interface FileBrowserData {
  folders: FolderItem[];
  files: DingTalkFile[];
  currentFolderId: string;
  spaceId: string;
  error?: string;
}

interface DingTalkFileBrowserProps {
  visible: boolean;
  onCancel: () => void;
  onSelect: (file: DingTalkFile) => void;
}

export const DingTalkFileBrowser: React.FC<DingTalkFileBrowserProps> = ({
  visible,
  onCancel,
  onSelect
}) => {
  const [loading, setLoading] = useState(false);
  const [currentFolderId, setCurrentFolderId] = useState<string>('0');
  const [folderPath, setFolderPath] = useState<Array<{ id: string; name: string }>>([{ id: '0', name: '根目录' }]);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [files, setFiles] = useState<DingTalkFile[]>([]);

  // 加载文件列表
  const loadFiles = useCallback(async (folderId: string = '0') => {
    setLoading(true);
    try {
      // 使用统一的 API 工具函数
      const apiPath = `/api/dingtalk/files?folderId=${folderId}&fileTypes=xlsx,xls`;
      const response = await apiFetch(apiPath, {
        method: 'GET'
      });

      if (!response.ok) {
        throw new Error(`获取文件列表失败: ${response.statusText}`);
      }

      const data: FileBrowserData = await response.json();

      if (data.error) {
        throw new Error(data.error);
      }

      setFolders(data.folders || []);
      setFiles(data.files || []);
      setCurrentFolderId(data.currentFolderId || folderId);
    } catch (error: any) {
      console.error('[文件浏览器] 加载失败:', error);
      message.error(`加载文件列表失败: ${error.message}`);
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // 打开文件夹
  const handleOpenFolder = useCallback((folder: FolderItem) => {
    const newPath = [...folderPath, { id: folder.folderId, name: folder.folderName }];
    setFolderPath(newPath);
    setCurrentFolderId(folder.folderId);
    loadFiles(folder.folderId);
  }, [folderPath, loadFiles]);

  // 返回上一级
  const handleGoBack = useCallback(() => {
    if (folderPath.length > 1) {
      const newPath = folderPath.slice(0, -1);
      const parentFolderId = newPath[newPath.length - 1].id;
      setFolderPath(newPath);
      setCurrentFolderId(parentFolderId);
      loadFiles(parentFolderId);
    }
  }, [folderPath, loadFiles]);

  // 选择文件
  const handleSelectFile = useCallback((file: DingTalkFile) => {
    onSelect(file);
    onCancel();
  }, [onSelect, onCancel]);

  // 刷新当前目录
  const handleRefresh = useCallback(() => {
    loadFiles(currentFolderId);
  }, [currentFolderId, loadFiles]);

  // 初始化加载
  useEffect(() => {
    if (visible) {
      setCurrentFolderId('0');
      setFolderPath([{ id: '0', name: '根目录' }]);
      loadFiles('0');
    }
  }, [visible, loadFiles]);

  return (
    <Modal
      title="从钉钉文档选择文件"
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={800}
      style={{ top: 20 }}
    >
      <div style={{ minHeight: 400, maxHeight: 600, display: 'flex', flexDirection: 'column' }}>
        {/* 路径导航 */}
        <div style={{ marginBottom: 16, padding: '8px 12px', background: '#f5f5f5', borderRadius: 4 }}>
          <Space>
            {folderPath.length > 1 && (
              <Button
                type="text"
                onClick={handleGoBack}
                size="small"
              >
                ← 返回
              </Button>
            )}
            <Text type="secondary" style={{ fontSize: 12 }}>
              当前路径: {folderPath.map(p => p.name).join(' / ')}
            </Text>
            <Button
              type="text"
              onClick={handleRefresh}
              size="small"
              loading={loading}
            >
              🔄 刷新
            </Button>
          </Space>
        </div>

        {/* 文件列表 */}
        <div style={{ flex: 1, overflow: 'auto', border: '1px solid #d9d9d9', borderRadius: 4 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">正在加载文件列表...</Text>
              </div>
            </div>
          ) : folders.length === 0 && files.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Empty />
              <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                当前目录为空
              </Text>
            </div>
          ) : (
            <div style={{ padding: 8 }}>
              {/* 文件夹列表 */}
              {folders.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                    文件夹 ({folders.length})
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {folders.map(folder => (
                      <div
                        key={folder.folderId}
                        onClick={() => handleOpenFolder(folder)}
                        style={{
                          padding: '8px 12px',
                          border: '1px solid #d9d9d9',
                          borderRadius: 4,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          minWidth: 150,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#1890ff';
                          e.currentTarget.style.backgroundColor = '#e6f7ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#d9d9d9';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span style={{ color: '#faad14', fontSize: 16 }}>📁</span>
                        <Text ellipsis style={{ flex: 1 }}>
                          {folder.folderName}
                        </Text>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 文件列表 */}
              {files.length > 0 && (
                <div>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>
                    Excel文件 ({files.length})
                  </Text>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {files.map(file => (
                      <div
                        key={file.fileId}
                        onClick={() => handleSelectFile(file)}
                        style={{
                          padding: '10px 12px',
                          border: '1px solid #d9d9d9',
                          borderRadius: 4,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#1890ff';
                          e.currentTarget.style.backgroundColor = '#e6f7ff';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#d9d9d9';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <span style={{ color: '#52c41a', fontSize: 18 }}>📊</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ display: 'block' }} ellipsis>
                            {file.fileName}
                          </Text>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {file.fileSize > 0 ? `${(file.fileSize / 1024).toFixed(2)} KB` : '未知大小'}
                          </Text>
                        </div>
                        <Button type="primary" size="small">
                          选择
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

